/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, useRef, useEffect, useContext, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { iframeResizer } from 'iframe-resizer';
import { useMergeRefs } from 'use-callback-ref';
import { useCallbackOne } from 'use-memo-one';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { createIframeBridge } from '@atlassian/bridge-core/v2';

import { useForgeUiAnalyticsEvent } from '../../../analytics/useForgeUiAnalyticsEvent';
import { createBridgeFeatures } from '../../../bridge/createBridgeFeatures';
import type { ForgeAppError } from '../../../bridge/ForgeAppError';
import type { TAnyBridgeMethod } from '../../../bridge/methods/types';
import type { OverlayController, BridgeControls } from '../../../bridge/types';
import { useBridgeContext } from '../../../bridge/useBridgeContext';
import { useBridgeServices } from '../../../bridge/useBridgeServices';
import { useLiveMethodMap } from '../../../bridge/useLiveMethodMap';
import { consolidateCustomBridge } from '../../../bridge/utils';
import { EnvironmentContext, useMetricsContext } from '../../../context';
import { captureAndReportError, useTracingContext } from '../../../error-reporting';
import { egressPermissionService } from '../../../utils/useEgressPermissionService';
import type { GQLExtension } from '../../../web-client/graphql/types';
import { useRuntimeErrorOverlay } from '../../../worker-runtime/useRuntimeErrorOverlay';
import { useRuntimeOverlayController } from '../../../worker-runtime/useRuntimeOverlayController';
import type { HTMLCustomIFrameElement, InnerIframeProps } from '../types';
import { useKeepIframeInteractiveDuringDrag } from '../useKeepIframeInteractiveDuringDrag';
import { getLoadingComponent } from '../utils/getLoadingComponent';
import { isTunnelSrc } from '../utils/getSrcUrl';
import { isInlineMacroPresentation } from '../utils/isInlineMacroPresentation';
import { useEmbeddedMacroConnection } from './useEmbeddedMacroConnection';
import { useInitialAppState } from './useInitialAppState';
import { useReloadOnMacroConfigChange } from './useReloadOnMacroConfigChange';

const styles = cssMap({
	box: {
		position: 'relative',
	},
	loadingOverlay: {
		width: '100%',
		height: '100%',
	},
});

// TODO: no styles defined for resizable modals yet.
// Only Jira enables resizing on modals at the moment.
const modalIframeStyles = css({
	height: '100%',
});

// Pre-`forge-ui-modal-rounded-corners`: a 3px radius that didn't match the modal's `radius.xlarge`.
const legacyModalRadiusStyles = css({
	borderRadius: token('radius.small', '3px'),
});

const iframeStyles = css({
	border: 'none',
	display: 'block',
	width: '100%',
});

// Hide via `visibility: hidden` (not `display: none`): some browsers (e.g. Safari) suspend scripts in
// non-rendered iframes, breaking app init / the bridge handshake. `height: 0` then collapses the box —
// otherwise the in-flow iframe falls back to the ~150px replaced-element default, leaving whitespace
// (e.g. under hidden UI Modifications apps).
const hiddenIframeStyles = css({
	visibility: 'hidden',
	height: 0,
	overflow: 'hidden',
});

// `isHidden` apps (UI Kit sandbox, UI Modifications, background scripts) are never shown, but
// `hiddenIframeStyles` leaves them in flow where `width: 100%` contributes the iframe's 300px
// replaced-element intrinsic width, clamping shrink-to-fit ancestors — inline macros, table cells —
// to 300px (ECOHELP-161574). Out of flow that contribution disappears while the guest stays
// rendered, so its timers, rAF and observers keep working. Not safe for the transient `loading` /
// `isInlineActive` states, whose iframe is about to be shown and must boot at its display width.
const structurallyHiddenIframeStyles = css({
	position: 'absolute',
	top: 0,
	left: 0,
});

const nonResizableIframeStyles = css({
	height: '100%',
});

const MACRO_MODULE_NAME = 'xen:macro';

// All permissions granted unconditionally — this is a fresh implementation with no legacy rollout gates.
const FRAME_PERMISSIONS_POLICY =
	'camera; clipboard-write; display-capture; fullscreen; microphone; local-network-access';

// Base sandbox rules always applied to every hosted-resource iframe.
const BASE_SANDBOX_RULES = [
	'allow-downloads',
	'allow-forms',
	'allow-modals',
	'allow-pointer-lock',
	'allow-same-origin',
	'allow-scripts',
] as const;

/**
 * Returns the sandbox attribute string for the iframe.
 * Apps with a wildcard egress rule are granted popup permissions so they can
 * open third-party URLs in new tabs/windows.
 */
const getSandboxAttribute = (
	egress: GQLExtension['egress'],
	sandboxPolicies: GQLExtension['sandboxPolicies'],
): string => {
	const rules: string[] = [...BASE_SANDBOX_RULES];
	try {
		if (egressPermissionService('FETCH_CLIENT_SIDE', egress).containsWildCardEgress()) {
			rules.push('allow-popups', 'allow-popups-to-escape-sandbox');
		}
		// eslint-disable-next-line no-unused-vars
	} catch (_) {
		// swallow error — treat as no wildcard egress
	}
	if (
		sandboxPolicies?.includes('allow_storage_access_by_user_activation') &&
		fg('allow_storage_access_by_user_activation_attribute')
	) {
		rules.push('allow-storage-access-by-user-activation');
	}
	return rules.join(' ');
};

export const InnerIframe: React.ForwardRefExoticComponent<
	InnerIframeProps & React.RefAttributes<HTMLCustomIFrameElement>
> = forwardRef<HTMLCustomIFrameElement, InnerIframeProps>(
	(
		{
			accountId,
			appId,
			bridge,
			customBridgeMethods: legacyCustomBridgeMethods,
			customBridge,
			customBridgeContext,
			components,
			contextIds,
			coreData,
			extension,
			extensionData,
			extensionPayload,
			extensionViewData,
			getContextToken,
			height = '100%',
			isHidden,
			isInModal,
			isResizable,
			loadingComponent,
			locale,
			onError,
			onLoad,
			src,
			setIsLoading,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			surfaceColor,
			theme,
			timezone,
			width = '100%',
			contextChangeListeners,
		},
		parentIframeRef,
	) => {
		const { overlay, overlayController, isInlineActive } = useRuntimeOverlayController();

		// Shared presenter: shows a Forge app error in the overlay + suppresses the host
		// renderer (UIKit) + provides i18n. Exposed as the `showAppError` bridge service
		// for the withErrorDisplay middleware to call on classified bridge errors.
		const showAppError = useRuntimeErrorOverlay({
			overlayController,
			extension,
			setShouldShowRenderer,
			onError,
		});

		const { userConsentRequired, renderThreeLOPrompt } = useInitialAppState({
			extension,
			appId,
			accountId,
			components,
			onLoad,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			overlayController,
		});

		const {
			getBridgeFeatures,
			getBridgeContext,
			destroy: cleanupBridge,
			setBridgeControl,
			onChange,
		} = useBridgeFeatures(
			{
				accountId,
				appId,
				bridge,
				contextIds,
				coreData,
				customBridgeContext,
				extension,
				extensionData,
				extensionPayload,
				extensionViewData,
				getContextToken,
				locale,
				surfaceColor,
				theme,
				timezone,
				contextChangeListeners,
			},
			overlayController,
			renderThreeLOPrompt,
			showAppError,
			legacyCustomBridgeMethods,
			customBridge,
		);

		const localIframeRef = useRef<HTMLCustomIFrameElement>(null);
		// Modals disable pointer events on every cross origin iframe while a drag
		// operation is in progress. That must not apply to a modal's own content.
		const keepInteractiveRef = useKeepIframeInteractiveDuringDrag({
			isEnabled: fg('forge-ui-modal-iframe-drag-fix'),
			isInModal,
		});
		const iframeRef = useMergeRefs([parentIframeRef, localIframeRef, keepInteractiveRef]);

		const iframeTitle = useIframeTitle(extension.properties?.name, appId);
		useEmbeddedMacroConnection({
			extension,
			getBridgeContext,
			legacyCustomBridgeMethods,
			customBridge,
		});

		const sendHandshakeMetric = useSendHandshakeMetric(extension);

		// `null` until the iframe fires its first `load` event; incremented on each subsequent
		// reload (e.g. tunnel-mode hot-reload). Both useIframeBridgeConnection and useIframeResizer
		// depend on it so they automatically re-run their setup+cleanup cycle on every reload.
		const [loadVersion, setLoadVersion] = useState<number | null>(null);
		const { isBridgeReady, reloadIframe } = useIframeBridgeConnection({
			iframeRef: localIframeRef,
			loadVersion,
			src,
			theme,
			surfaceColor,
			getBridgeFeatures,
			cleanupBridge,
			onLoad,
			setBridgeControl,
			sendHandshakeMetric,
		});
		const handleIframeLoad = useCallbackOne(() => {
			setLoadVersion((v) => (v ?? 0) + 1);
		}, []);

		// TODO: temporary workaround — remove this call and delete useReloadOnMacroConfigChange once
		// every macro observes config changes through the event-based API (e.g. useConfig). At that
		// point we do not want to reload the iframe at all. See that file.
		useReloadOnMacroConfigChange({
			extension,
			extensionData,
			extensionPayload,
			onChange,
			reloadIframe,
		});

		const loading = !isBridgeReady;

		// Same predicate that drives `hiddenIframeStyles` below: true only when the iframe is shown.
		const isIframeVisible = !(loading || isInlineActive || isHidden);
		// Gate the resizer on visibility so it never measures a hidden iframe (wrong-width modals).
		// Off: legacy behaviour, resizer inits on `load`.
		const isResizerReady = fg('forge-ui-bridge-v2-frame-resizer-fix') ? isIframeVisible : true;

		useIframeResizer({
			iframeRef: localIframeRef,
			loadVersion,
			isResizable,
			isReady: isResizerReady,
			height,
			isDashboardFilter:
				extension.type === 'dashboards:filter' && extensionPayload?.filter !== undefined,
			isEditing: extensionData?.isEditing ?? extensionPayload?.isEditing,
			isInModal,
			layout: extension.properties.layout,
			type: extension.type,
		});

		useEffect(() => {
			setIsLoading?.(loading);
		}, [loading, setIsLoading]);

		const sandbox = getSandboxAttribute(extension.egress, extension.sandboxPolicies);

		return (
			<Box
				xcss={styles.box}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					height: isResizable || isInlineActive ? undefined : height,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					width,
				}}
			>
				<IframeLoadingOverlay
					loading={loading}
					isInlineActive={isInlineActive}
					isHidden={isHidden}
					loadingComponent={loadingComponent}
				/>
				{overlay}
				{!userConsentRequired && (
					<iframe
						data-testid="hosted-resources-iframe"
						data-forge-iframe
						css={[
							iframeStyles,
							isInModal && modalIframeStyles,
							isInModal && !fg('forge-ui-modal-rounded-corners') && legacyModalRadiusStyles,
							!isResizable && nonResizableIframeStyles,
							// Applied last so its `height: 0` overrides `nonResizableIframeStyles`' `height: 100%`.
							(loading || isInlineActive || isHidden) && hiddenIframeStyles,
							isHidden &&
								fg('forge-ui-bridge-v2-frame-resizer-fix') &&
								structurallyHiddenIframeStyles,
						]}
						sandbox={sandbox}
						allow={FRAME_PERMISSIONS_POLICY}
						src={src}
						ref={iframeRef}
						onLoad={handleIframeLoad}
						title={iframeTitle}
					/>
				)}
			</Box>
		);
	},
);

type IframeLoadingOverlayProps = {
	isHidden: InnerIframeProps['isHidden'];
	isInlineActive: boolean;
	loading: boolean;
	loadingComponent: InnerIframeProps['loadingComponent'];
};

/**
 * Renders a loading placeholder while the bridge handshake is in progress.
 *
 * Suppressed in two cases where it would be redundant or misleading:
 * - `isInlineActive`: an interactive overlay (e.g. ThreeLOPrompt) is occupying the slot —
 *   the user is already looking at meaningful UI, so the spinner would be noise underneath it.
 * - `isHidden`: UIKit apps pass `isHidden={true}` — their visible content is rendered by `RendererNext` in the
 *   host, which owns its own loading state. Showing a spinner here would be a duplicate.
 */
const IframeLoadingOverlay = ({
	loading,
	isInlineActive,
	isHidden,
	loadingComponent,
}: IframeLoadingOverlayProps) => {
	if (!loading || isInlineActive || isHidden) {
		return null;
	}
	return <Box xcss={styles.loadingOverlay}>{getLoadingComponent(loadingComponent)}</Box>;
};

const useIframeTitle = (appName: string | undefined, appId: string): string => {
	return `Embedded app content for ${appName ?? appId}`;
};

type UseBridgeFeaturesInput = Pick<
	InnerIframeProps,
	| 'accountId'
	| 'appId'
	| 'bridge'
	| 'contextIds'
	| 'coreData'
	| 'customBridgeContext'
	| 'extension'
	| 'extensionData'
	| 'extensionPayload'
	| 'extensionViewData'
	| 'getContextToken'
	| 'locale'
	| 'surfaceColor'
	| 'theme'
	| 'timezone'
	| 'contextChangeListeners'
>;

type UseIframeBridgeConnectionInput = {
	/** Tears down the bridge connection and all associated context resources. */
	cleanupBridge: () => void;
	getBridgeFeatures: () => ReturnType<typeof createBridgeFeatures>;
	iframeRef: React.RefObject<HTMLCustomIFrameElement>;
	/**
	 * `null` until the iframe fires its first `load` event; incremented on each subsequent
	 * reload. Any non-null value triggers (or re-triggers) the bridge handshake. Using a
	 * counter rather than a boolean ensures React always detects a change even on rapid
	 * consecutive reloads.
	 */
	loadVersion: number | null;
	onLoad: InnerIframeProps['onLoad'];
	/**
	 * Called by bridge-core v2 once the handshake resolves. `success=true` means the
	 * connection is live; `success=false` means it failed and `error` contains the cause.
	 * Used to fire analytics and, on failure, report to Sentry.
	 */
	sendHandshakeMetric: (success: boolean, error?: Error) => void;
	setBridgeControl: (controls: BridgeControls) => void;
	src: string;
	surfaceColor: InnerIframeProps['surfaceColor'];
	theme: InnerIframeProps['theme'];
};

/**
 * Manages the bridge-core v2 connection lifecycle for a hosted-resource iframe.
 *
 * Responsibilities:
 * - Opens {@link createIframeBridge} each time `loadVersion` increments (initial load and
 *   subsequent reloads, e.g. tunnel-mode hot-reload).
 * - Resets `isBridgeReady` to `false` at the start of each reload cycle so the loading
 *   overlay is shown while the new handshake is in progress.
 * - Closes any in-flight or live connection before the new one is established.
 * - Tears down the bridge context fully on component unmount via `cleanupBridge`.
 */
const useIframeBridgeConnection = ({
	iframeRef,
	loadVersion,
	src,
	theme,
	surfaceColor,
	getBridgeFeatures,
	cleanupBridge,
	onLoad,
	setBridgeControl,
	sendHandshakeMetric,
}: UseIframeBridgeConnectionInput): {
	isBridgeReady: boolean;
	reloadIframe: () => void;
} => {
	const [isBridgeReady, setIsBridgeReady] = useState(false);
	const { trackExtensionRemounted } = useForgeUiAnalyticsEvent();
	// True from a reload request until the resulting `load` event, so bursts coalesce.
	const reloadPendingRef = useRef(false);

	// Keyed solely on loadVersion: other inputs are stable after mount, and theme/surfaceColor
	// changes flow through useBridgeFeatures' onChange listener (which calls controls.setTheme
	// on the live connection) rather than re-triggering the handshake.
	useEffect(() => {
		if (loadVersion === null) {
			return;
		}

		reloadPendingRef.current = false;
		setIsBridgeReady(false);

		const contentWindow = iframeRef.current?.contentWindow;
		if (!contentWindow) {
			return;
		}

		let cancelled = false;

		createIframeBridge({
			withWindow: contentWindow,
			withDomain: new URL(src).origin,
			features: getBridgeFeatures(),
			theme,
			surfaceColor,
			sendHandshakeMetric,
		})
			.open()
			.then((controls) => {
				// `reloadPendingRef` covers a reload requested while this handshake was in flight:
				// the effect has not re-run yet (loadVersion is unchanged), so `cancelled` is still
				// false, but these controls belong to the document we just navigated away from.
				if (cancelled || reloadPendingRef.current) {
					controls.close();
					return;
				}
				setBridgeControl(controls);
				setIsBridgeReady(true);
				onLoad?.();
				// Tunnel hot-reloads are expected dev-loop noise; only track reloads in non-tunnel src.
				if (loadVersion > 1 && !isTunnelSrc(src)) {
					trackExtensionRemounted({ mountCount: loadVersion });
				}
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('Failed to open iframe bridge', err);
			});

		return () => {
			cancelled = true;
		};
	}, [loadVersion]); // eslint-disable-line react-hooks/exhaustive-deps

	// Full teardown on unmount: BridgeContextObject.destroy() closes any live connection.
	// Effect body is intentionally empty — `cleanupBridge` is returned as the cleanup function,
	// invoked by React on unmount (and when `cleanupBridge` changes, which it shouldn't in practice).
	useEffect(() => cleanupBridge, [cleanupBridge]);

	// TODO: Note, this is a temporary solution to allow reload (for macros)
	// we should try avoid and disable this pattern in the future.
	// Re-navigates the iframe to `src`, which fires `load` and drives a fresh handshake via
	// `loadVersion`. `location.replace` is cross-origin accessible and, unlike assigning `src`,
	// does not push a session-history entry.
	const reloadIframe = useCallbackOne(() => {
		const contentWindow = iframeRef.current?.contentWindow;
		if (!contentWindow || reloadPendingRef.current) {
			return;
		}
		reloadPendingRef.current = true;
		try {
			contentWindow.location.replace(src);
		} catch (e) {
			// Navigation never started. Leave the current document alone rather than hiding it, and
			// clear the pending flag so later config changes are not silently dropped.
			reloadPendingRef.current = false;
			return;
		}
		// After `replace` so the failure path above leaves the iframe visible, but still well before
		// the new document's `load` event — otherwise stale content shows and then collapses.
		setIsBridgeReady(false);
	}, [src, iframeRef]);

	return { isBridgeReady, reloadIframe };
};

type UseIframeResizerInput = {
	height: string;
	iframeRef: React.RefObject<HTMLCustomIFrameElement>;
	isDashboardFilter: boolean;
	isEditing?: boolean;
	isInModal: InnerIframeProps['isInModal'];
	/** Whether the resizer may initialise. Tracks iframe visibility; always `true` when the gate is off. */
	isReady: boolean;
	isResizable: InnerIframeProps['isResizable'];
	layout: string | undefined;
	/**
	 * `null` until the iframe fires its first `load` event; incremented on each subsequent
	 * reload. While `null`, `iframeResizer` is not initialised. On reload the effect re-runs:
	 * cleanup calls `removeListeners()` on the old document, then a fresh `iframeResizer(...)`
	 * call attaches to the new document.
	 */
	loadVersion: number | null;
	type: string;
};

/**
 * Initialises `iframe-resizer` on the hosted-resource iframe each time the iframe loads
 * (initial load and subsequent reloads such as tunnel-mode hot-reload).
 *
 * Re-runs whenever `loadVersion` increments: the effect cleanup calls `removeListeners()`
 * on the previous document and the effect body attaches a fresh resizer to the new one.
 * Mirrors v1 behaviour in `useBridge.tsx` where `iframeResizer(...)` is called inside the
 * `onLoad` callback so it naturally re-runs on every reload.
 *
 * `sizeWidth` matches v1 `useBridge.tsx`: enabled for a non-JSM modal (`isInModal` + extension
 * type) or an inline macro.
 *
 * Note on reload re-init: `iframe-resizer`'s content-window script auto-handshakes when a
 * fresh guest document loads, so re-initing on the host side is not strictly required by
 * the library. We do it for parity with v1 and to keep the host-side config explicitly
 * fresh on every load. Future improvement: lean on the library's built-in reload tolerance
 * and collapse this into a mount-only effect.
 */
const useIframeResizer = ({
	iframeRef,
	loadVersion,
	isResizable,
	isReady,
	height,
	isEditing,
	isDashboardFilter,
	isInModal,
	layout,
	type,
}: UseIframeResizerInput): void => {
	// Deps are intentionally narrowed to [loadVersion, isResizable, isReady]. `height`, `isInModal`,
	// `isEditing`, `layout`, and `type` are captured by closure from whichever render last fired a `load`
	// event, matching v1's "init once per load with the values current at that moment" behaviour.
	// `isReady` re-inits (or tears down) the resizer on visibility changes; it is constant `true`
	// when the gate is off. `iframeRef` is a stable ref object and never triggers re-runs.
	//
	// Latent assumption: this hook does NOT react to mid-lifecycle changes of those values. They
	// configure `minHeight` and `sizeWidth` at init time and the library has no runtime API to
	// update them on a live instance. In current usage they are effectively static after mount
	// (layout/type/isEditing/isDashboardFilter/isInModal come from extension + host chrome; height
	// is parent-supplied). If a product ever mutates them at runtime while `isResizable=true`,
	// re-init on change or replace the library with one that supports in-place updates.
	useEffect(() => {
		if (!isResizable || loadVersion === null || !isReady) {
			return;
		}
		const iframeEl = iframeRef.current;
		if (!iframeEl) {
			return;
		}

		// JSM modals are always height resizable, but not width resizable.
		// TODO: Can remove this once aligned with JSM on width resizing
		const isInNonJSMResizableModal = isInModal && !type?.startsWith('jiraServiceManagement');
		const isInInlineMacro =
			type === MACRO_MODULE_NAME && isInlineMacroPresentation({ isEditing, layout });

		const resizeDashboardFilter =
			fg('platform_avp_dashboards_filter_size_width') === true && isDashboardFilter;

		const sizeWidth = isInNonJSMResizableModal || isInInlineMacro || resizeDashboardFilter;

		iframeResizer(
			{
				heightCalculationMethod: 'bodyScroll',
				widthCalculationMethod: 'bodyScroll',
				scrolling: true,
				// @ts-expect-error minHeight accepts a CSS string at runtime but the TS type only allows number
				minHeight: height,
				sizeWidth,
				initCallback: (iframe) => {
					iframe?.iFrameResizer?.resize();
				},
			},
			iframeEl,
		);
		return () => {
			iframeEl?.iFrameResizer?.removeListeners();
		};
	}, [loadVersion, isResizable, isReady]); // eslint-disable-line react-hooks/exhaustive-deps
};

const useSendHandshakeMetric = (
	extension: InnerIframeProps['extension'],
): ((success: boolean, error?: Error) => void) => {
	const { page } = useMetricsContext();
	const environment = useContext(EnvironmentContext);
	const tracing = useTracingContext();
	const { trackHandShakeMetric } = useForgeUiAnalyticsEvent();

	// Keep a ref so the callback always reads the latest extension without needing
	// it as a dependency — avoids recreating the callback on every render when the
	// extension object reference changes but its identity (id, type, etc.) has not.
	const extensionRef = useRef(extension);
	extensionRef.current = extension;

	return useCallbackOne(
		(success: boolean, error?: Error) => {
			trackHandShakeMetric({ source: page, success, error });
			if (error) {
				const { id, type, environmentType, appOwner, properties } = extensionRef.current;
				const handshakeError =
					error instanceof Error
						? new Error(`Bridge handshake error: ${error.message}`)
						: new Error('Unknown bridge handshake error');
				void captureAndReportError({
					error: handshakeError,
					environment,
					errorExtensionDetails: {
						id,
						type,
						environmentType,
						appOwnerAccountId: appOwner?.accountId,
						properties,
					},
					page,
					runtime: 'iframev2',
					tracing,
				});
			}
		},
		[page, environment, tracing, trackHandShakeMetric],
	);
};

const useBridgeFeatures = (
	props: UseBridgeFeaturesInput,
	overlayController: OverlayController,
	renderThreeLOPrompt: (authUrl: string, retry: () => Promise<void>) => Promise<void>,
	showAppError: (error: ForgeAppError) => void,
	// legacyCustomBridgeMethods and customBridge are explicitly not part of props passed to useBridgeContext,
	// because they are extension mechanisms for products, not core bridge concerns.
	legacyCustomBridgeMethods?: Record<string, (data: unknown) => Promise<unknown>>,
	customBridge?: Record<string, TAnyBridgeMethod>,
) => {
	// Legacy (v1) methods are tracked live because some modules recreate them after initial render.
	const getLegacyCustomBridgeMethods = useLiveMethodMap(legacyCustomBridgeMethods);

	const environment = useContext(EnvironmentContext);
	const { page } = useMetricsContext();
	// Assembles host-side capabilities (invoker, fetch client, tracing, etc.) for bridge methods.
	// Returns a getter rather than a snapshot so services are always current at call time.
	const { getServices } = useBridgeServices(
		props,
		overlayController,
		renderThreeLOPrompt,
		showAppError,
	);

	// [Important] getBridgeContext is stable and does not change across the lifecycle of the component,
	// so it is safe to include it in the dependency array of createBridgeFeatures without causing
	// unnecessary re-renders or re-creations of the bridge features.
	const { getBridgeContext, destroy, onChange, setBridgeControl } = useBridgeContext(
		{ ...props, environment, page, runtime: 'iframev2' },
		getServices,
		props.contextChangeListeners,
	);

	// Registered once — `onChange` is stable and `destroy()` clears the listener on unmount.
	useEffect(() => {
		return onChange((_prev, next, changes, getContext) => {
			// Push theme/surfaceColor changes to the open bridge connection whenever they change.
			// Custom UI only — we only do this for iframe based runtime, as this is only
			// relevant for Custom UI app, as the Custom UI app will need to update the theme style assets.
			if (changes.theme || changes.surfaceColor) {
				getContext().bridgeControls?.setTheme(
					next.theme ?? null,
					next.surfaceColor ?? null,
					next.environment,
				);
			}
		});
	}, [onChange]);

	return {
		destroy,
		getBridgeContext,
		onChange,
		getBridgeFeatures: () =>
			createBridgeFeatures(getBridgeContext, (baseBridgeMethods) =>
				consolidateCustomBridge(
					baseBridgeMethods,
					getLegacyCustomBridgeMethods(),
					// v2 customBridge is a stable-reference contract — passed through directly, not tracked live.
					customBridge,
				),
			),
		setBridgeControl,
	};
};
